// Pass a maxLineWidth and an array of objects representing words. If
// maxLineWidth is falsy, wrapping is only done at blocks and line breaks. Each
// word object has keys:
// - width: width of this node.
// - isSpace: should this node function as space? Default false. Note that
//   consecutive words which are not blocks, spaces, or line breaks will attach
//   and wrap together.
// - isManualSpace: is this node a manual space? Default false. No effect
//   unless isSpace is true.
// - isLineBreak: Is this node a line break? Default false. Ignored if the node
//   is also attached to next.
// - isBlock: Is this a block node? Default false.
// Returns an object with keys:
// - lines: array of lines, where lines are an array of numbers representing
//   indices of the input array.
// - errors: array of word blocks, which is an array of numbers representing
//   all indices of an attached word (a single element if no attaching).
//   Each of these word blocks is too long to fit on one line.
const doWordWrap = function(maxLineWidth, wordList) {
	// Array of lines found so far.
	const lineList = [];
	// Array of bad node indices found so far.
	const badNodes = [];
	// Indices in the current line so far.
	let currLine = [];
	// Current word indices including attachments.
	let currWordBlock = [];

	// Helper. Get the length of a list of words passed as indices.
	const getIndListWidth = function(indList) {
		return getWordListWidth(indList.map(i => wordList[i]));
	};

	// Helper. Finishes the line in currLine and starts a new one.
	const finishLine = function() {
		const trimmed = trimRWordList(currLine.map(i => wordList[i]));
		if (currLine.length) {
			lineList.push(currLine.slice(0, trimmed.length));
		}
		currLine = [];
	};

	// Helper. Returns whether this word should attach to the next one in the list.
	const attachesToNext = function(ind) {
		return (
			wordList[ind + 1] &&
			isSticky(wordList[ind]) &&
			isSticky(wordList[ind + 1])
		);
	};

	for (let i = 0; i < wordList.length; i += 1) {
		if (wordList[i].isBlock) {
			currWordBlock.push(i);
			finishLine();
			lineList.push([].concat(currWordBlock));
			currLine = [];
			currWordBlock = [];
		} else if (attachesToNext(i)) {
			currWordBlock.push(i);
		} else {
			currWordBlock.push(i);

			// Does the current word block overflow on its own?
			const isLongWord =
				maxLineWidth && maxLineWidth < getIndListWidth(currWordBlock);
			if (isLongWord) {
				badNodes.push([].concat(currWordBlock));
				// Add all space nodes before we wrap below.
				while (wordList[i + 1] && wordList[i + 1].isSpace) {
					i += 1;
					currWordBlock.push(i);
				}
			}

			// Does this word send the current line over the maximum width?
			const overflowsLine =
				currLine.length &&
				maxLineWidth &&
				maxLineWidth < getIndListWidth(currLine.concat(currWordBlock));
			if (overflowsLine) {
				finishLine();
			}

			// If first word in new line is a non-manual space, don't add it to new
			// line.
			const currNontrivialWords = currWordBlock.filter(wordInd => {
				return !isSoftSpace(wordList[wordInd]);
			});
			if (!currNontrivialWords.length && !currLine.length) {
				currWordBlock = [];
			}

			currLine = currLine.concat(currWordBlock);
			currWordBlock = [];

			// Is this a line break?
			if (wordList[i].isLineBreak) {
				finishLine();
			}
		}
	}

	currLine = currLine.concat(currWordBlock);
	finishLine();

	return {
		lines: lineList,
		errors: badNodes,
	};
};

// Computes the width of a list of word objects.
const getWordListWidth = function(wordList) {
	return trimRWordList(wordList)
		.map(w => w.width)
		.reduce((a, b) => a + b, 0);
};

// Trim spaces from the end of a word list.
const trimRWordList = function(wordList) {
	let lastInd = wordList.length - 1;
	while (wordList[lastInd] && isTrimmable(wordList[lastInd])) {
		lastInd -= 1;
	}
	return wordList.slice(0, lastInd + 1);
};

// Returns whether the node is a space or a line break and can be trimmed from
// the end of a line.
const isTrimmable = function(node) {
	return node.isSpace || node.isLineBreak;
};

// Returns whether the node is a soft space that can be ignored for purposes of
// line breaking.
const isSoftSpace = function(node) {
	return node.isSpace && !node.isManualSpace && !node.isLineBreak;
};

// Return true if this node can attach to the node on either side for
// wordwrapping purposes.
const isSticky = function(node) {
	return !node.isSpace && !node.isLineBreak && !node.isBlock;
};

module.exports = {
	doWordWrap,
	getWordListWidth,
};
